import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { status401, status403 } from 'helpers/constants/localStorage';
import { acquireMediaclipHub, initializeMediaclip, renewTokenCallback } from 'helpers/mediaclip/mediaclip-hub';
import { clearMediaclipToken, getMediaclipToken } from 'helpers/mediaclip/mediaclipToken';
import { useProjectSettings } from 'frontastic';

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY = 500;

const getThumbnailSrc = (hub: any, projectId: string) => {
  return new Promise<string>((resolve, reject) => {
    hub
      .getProjectThumbnailSrc(projectId)
      .done((url: string) => resolve(url))
      .fail((err: any) => reject(err));
  });
};

const useMediaClipThumbnail = (mediaClipProjectId: string | undefined) => {
  const scriptLoadedRef = useRef<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { projectSettings } = useProjectSettings();
  const mediaclipRegion = projectSettings?.mediaclipRegion as string

  const fetchTokenIfNeeded = useCallback((forceRefresh = false) => getMediaclipToken(forceRefresh), []);

  const hubOptions = useMemo(
    () => ({
      renewTokenCallback,
      onError: (err: unknown) => console.error('Hub init error:', err),
    }),
    [],
  );

  /**
   * Re-initializes in place after an auth retry. Deliberately not a new lease: the caller
   * already holds one for the lifetime of this hook, and taking a second here would leak a
   * reference the cleanup never releases.
   */
  const initHub = useCallback(
    async (token: string) => initializeMediaclip(scriptLoadedRef, token, mediaclipRegion, hubOptions),
    [mediaclipRegion, hubOptions],
  );

  const isAuthError = (error: any) =>
  error?.status === status401 ||
  error?.status === status403 ||
  error?.statusCode === status401 ||
  error?.statusCode === status403 ||
  error?.response?.status === status401 ||
  error?.response?.status === status403 ||
  error?.message?.includes?.(status401) ||
  error?.message?.includes?.(status403);

const executeWithAuthRetry = async <T,>(fn: (hub: any) => Promise<T>): Promise<T | null> => {
  try {
    const hub = (globalThis as any).mediaclip?.hub;
    return await fn(hub);
  } catch (error) {
    if (!isAuthError(error)) throw error;

    clearMediaclipToken();

    const newToken = await fetchTokenIfNeeded(true);
    if (!newToken) return null;

    const newHub = await initHub(newToken);

    return await fn(newHub);
  }
};

const runThumbnailRequest = async (
  hub: any,
  projectId: string,
  attempt: number,
  cancelled: { value: boolean },
  timeoutRef: { current: ReturnType<typeof setTimeout> | null }
) => {
  setLoading(true);

  try {
    const thumbUrl = await executeWithAuthRetry((hubInstance) => getThumbnailSrc(hubInstance, projectId));

    if (!cancelled.value && thumbUrl) {
      setImageSrc(thumbUrl);
      setLoading(false);
    }

  } catch (error:any) {
    if (cancelled.value) return;

    if (error?.status === 0) {
      setImageSrc('');
      setLoading(false);
      return;
    }

    if (attempt >= MAX_RETRY_ATTEMPTS) {
      console.error('Thumbnail failed after retries:', error);
      setLoading(false);
      return;
    }

    const delay = Math.pow(2, attempt - 1) * RETRY_BASE_DELAY;

    timeoutRef.current = setTimeout(() => {
      const latestHub = (globalThis as any).mediaclip?.hub;
      void runThumbnailRequest(latestHub || hub, projectId, attempt + 1, cancelled, timeoutRef);
    }, delay);
  }
};

const fetchThumbnail = useCallback((projectId: string) => {
  const cancelled = { value: false };

  const checkHubAndRun = () => {
    const hub = (globalThis as any).mediaclip?.hub;

    if (!hub?.getProjectThumbnailSrc) {
      timeoutRef.current = setTimeout(checkHubAndRun, 300);
      return;
    }

    void runThumbnailRequest(hub, projectId, 1, cancelled, timeoutRef);
  };

  checkHubAndRun();

  return () => {
    cancelled.value = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setLoading(false);
  };
}, [fetchTokenIfNeeded, initHub]);

  useEffect(() => {
    if (!mediaClipProjectId || !mediaclipRegion) return;

    let cleanupFn: (() => void) | undefined;
    let releaseHub: (() => void) | undefined;
    let cancelled = false;

    const execute = async () => {
      const token = await fetchTokenIfNeeded();
      if (cancelled || !token) return;

      const lease = await acquireMediaclipHub(scriptLoadedRef, token, mediaclipRegion, hubOptions);

      // Unmounted while the hub was initializing: hand the lease straight back, otherwise
      // the keep-alive timer outlives this component with nothing left to release it.
      if (cancelled) {
        lease.release();
        return;
      }

      releaseHub = lease.release;

      cleanupFn = fetchThumbnail(mediaClipProjectId);
    };

    void execute();

    return () => {
      cancelled = true;
      cleanupFn?.();
      releaseHub?.();
    };
  }, [mediaClipProjectId, mediaclipRegion, fetchTokenIfNeeded, hubOptions, fetchThumbnail]);

  return { imageSrc, loading };
};

export default useMediaClipThumbnail;
